/**
 * Material-table selection helpers.
 * - Header checkbox should select the current page only.
 * - "Select All" button should select every selectable row (all pages / filtered set).
 */

export function getDataManager(tableRef) {
  return tableRef?.current?.dataManager || null;
}

export function getSelectableRows(tableRef, fallbackRows = []) {
  const dm = getDataManager(tableRef);
  if (!dm) return fallbackRows || [];
  if (Array.isArray(dm.searchedData) && dm.searchedData.length) {
    return dm.searchedData;
  }
  if (Array.isArray(dm.data) && dm.data.length) {
    return dm.data;
  }
  return fallbackRows || [];
}

export function getCheckedRows(tableRef, fallbackRows = []) {
  return getSelectableRows(tableRef, fallbackRows).filter(
    (row) => row?.tableData?.checked
  );
}

function setRowsChecked(rows, checked) {
  (rows || []).forEach((row) => {
    if (!row.tableData) row.tableData = {};
    if (row.tableData.disabled) {
      row.tableData.checked = false;
      return;
    }
    row.tableData.checked = checked;
  });
}

function refreshTableSelection(tableRef, setSelectedRows, fallbackRows = []) {
  const table = tableRef?.current;
  const dm = getDataManager(tableRef);
  if (!table || !dm) {
    setSelectedRows(checkedFallback(fallbackRows));
    return;
  }
  const selected = getCheckedRows(tableRef, fallbackRows);
  dm.selectedCount = selected.length;
  table.setState(dm.getRenderState(), () => {
    setSelectedRows(selected);
  });
}

function checkedFallback(rows) {
  return (rows || []).filter((row) => row?.tableData?.checked);
}

/** Current page only (for header checkbox). */
export function selectCurrentPage(tableRef, setSelectedRows, checked, fallbackRows = []) {
  const dm = getDataManager(tableRef);
  if (!dm) {
    setSelectedRows(checked ? fallbackRows.slice(0, 10) : []);
    return;
  }
  setRowsChecked(dm.searchedData || dm.data || [], false);
  if (checked) {
    setRowsChecked(dm.pagedData || [], true);
  }
  refreshTableSelection(tableRef, setSelectedRows, fallbackRows);
}

/** All selectable rows across pages (filtered/search set). */
export function selectAllRows(tableRef, setSelectedRows, checked, fallbackRows = []) {
  const dm = getDataManager(tableRef);
  if (!dm) {
    setSelectedRows(checked ? fallbackRows : []);
    return;
  }
  const rows = dm.searchedData?.length ? dm.searchedData : dm.data || fallbackRows;
  setRowsChecked(rows, checked);
  refreshTableSelection(tableRef, setSelectedRows, fallbackRows);
}

export function clearTableSelection(tableRef, setSelectedRows) {
  selectAllRows(tableRef, setSelectedRows, false, []);
}

/**
 * Override MaterialTable header "select all" so it only affects the current page.
 * Call once the tableRef is populated (e.g. after data load / in render effect).
 */
export function bindPageOnlyHeaderSelection(tableRef, setSelectedRows, onAfterChange) {
  const table = tableRef?.current;
  if (!table || typeof table.onAllSelected !== "function") return;

  table.onAllSelected = (checked) => {
    selectCurrentPage(tableRef, setSelectedRows, checked);
    if (typeof onAfterChange === "function") {
      onAfterChange(checked);
    }
  };
}

export function isAllSelectableSelected(tableRef, selectedRows, fallbackRows = []) {
  const selectable = getSelectableRows(tableRef, fallbackRows);
  if (!selectable.length) return false;
  if ((selectedRows || []).length < selectable.length) return false;
  const selectedIds = new Set(
    (selectedRows || []).map((row) => String(row?._id ?? row?.tableData?.id))
  );
  return selectable.every((row) =>
    selectedIds.has(String(row?._id ?? row?.tableData?.id))
  );
}
